import React, { useState, useRef } from 'react';
import { AppBar, Button, Tab, Tabs, Typography } from '@material-ui/core';
import EditWell from './EditWell';
import { makeStyles } from '@material-ui/styles';
import Auth from '../../util/Auth';
import PERMISSIONS_LIST from '../../util/RolesEnum';
import { useSnackbar } from 'notistack/build';
import PlatMap from './PlatMap';
import Toolbar from '@material-ui/core/Toolbar';
import WellMapCard from '../../components/wells/WellMapCard';
import { GET_WELL, EDIT_WELL } from '../../components/wells/WellQueries';
import { SET_TRACT_GEOMETRY } from '../../components/tracts/TractQueries';
import { useQuery, useMutation } from '@apollo/client';
import LoadingView from '../../components/LoadingView';

const hasPermissions = Auth.hasRole(PERMISSIONS_LIST.WRITE.WRITE_WELLS);

export const WellFormStyles = makeStyles((theme) => ({
  root: {
    paddingBottom: 50,
  },
  TextField: {
    display: 'block',
    marginTop: 10,
    marginBottom: 10,
    paddingRight: '1em',
  },
  Switch: {
    margin: 0,
  },
  BottomBar: {
    display: hasPermissions ? 'flex' : 'none',
    justifyContent: 'space-between',
  },
  positives: {
    display: 'flex',
    flexDirection: 'row',
  },
  SubmitButton: {
    display: hasPermissions ? 'inline-block' : 'none',
    maxWidth: '200px',
    maxHeight: '50px',
    minWidth: '200px',
    float: 'left',
  },
  DeleteButton: {
    display: hasPermissions ? 'inline-block' : 'none',
    maxWidth: '200px',
    maxHeight: '50px',
    minWidth: '200px',
    float: 'right',
  },
  FormControlLabel: {
    paddingRight: '5em',
  },
  Select: {
    display: 'block',
  },
  FullWidthTextBox: {
    marginBottom: 20,
    marginTop: 20,
  },
  paper: {
    padding: 25,
    marginBottom: '1rem',
    marginTop: '1rem',
  },
  title: {
    color: '#1976d2',
    fontSize: '1.75rem',
    textAlign: 'left',
    textDecoration: 'underline',
  },
}));

function TabContainer(props) {
  return (
    <Typography component='div' style={{ paddingTop: 20 }}>
      {props.children}
    </Typography>
  );
}

const WellTabPage = ({ match, history, location }) => {
  const initialTab = parseInt(new URLSearchParams(location.search).get('tab') || '0', 10);
  const [value, setValue] = useState(initialTab);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [mapEditMode, setMapEditMode] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const wellKmzUploadRef = useRef(null);

  const { data: wellMapData, loading: wellMapLoading } = useQuery(GET_WELL, {
    variables: { id: match.params.wellID },
  });
  const [saveWellCoords] = useMutation(EDIT_WELL);
  const [setTractGeometry] = useMutation(SET_TRACT_GEOMETRY);

  const well = wellMapData?.Well?.[0] ?? null;
  const tracts = well?.drilledOn ?? [];

  // Save just one of the two well points (surface or bottom) without disturbing
  // the other. EDIT_WELL needs both pairs, so we pass through the unchanged side.
  const saveWellPoint = (which, { lat, lng }) => {
    if (!well) return;
    const variables = {
      id: well.id,
      apiNumber: well.apiNumber,
      latitude: String(well.latitude ?? lat),
      longitude: String(well.longitude ?? lng),
      latitudeBottom: String(well.latitudeBottom ?? lat),
      longitudeBottom: String(well.longitudeBottom ?? lng),
    };
    if (which === 'surface') {
      variables.latitude = String(lat);
      variables.longitude = String(lng);
    } else {
      variables.latitudeBottom = String(lat);
      variables.longitudeBottom = String(lng);
    }
    saveWellCoords({ variables })
      .then(() => enqueueSnackbar(`${which === 'surface' ? 'Surface' : 'Bottom hole'} position saved`, { variant: 'success' }))
      .catch((err) => enqueueSnackbar(err.toString(), { variant: 'error' }));
  };

  const handleTractPolygonChange = (tractId, newCoords) => {
    const geometry = JSON.stringify(newCoords.map(([lat, lng]) => [lng, lat]));
    setTractGeometry({ variables: { tractId, geometry } })
      .then(() => enqueueSnackbar('Tract polygon saved', { variant: 'success' }))
      .catch((err) => enqueueSnackbar(err.toString(), { variant: 'error' }));
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      overflowY: 'auto',
      paddingLeft: 1,
    }}>
      <AppBar position='fixed'
              style={{
                display: 'flex',
                marginBottom: 5,
                paddingTop: value < 2 ? 68 : 56,
                height: value < 2 ? 150 : 120,
              }}
      >
        <Toolbar
          style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            alignSelf: 'flex-start',
            paddingLeft: 215,
          }}
        >
          <Tabs
            value={value}
            onChange={(event, newValue) => setValue(newValue)}
          >
            <Tab label='Main' />
            <Tab label='Map' />
            <Tab label='Plat' />
          </Tabs>
          {value === 0 && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', alignSelf: 'center' }}>
              <Button
                size='small'
                onClick={() => setDeleteModalOpen(true)}
                variant='contained'
                color='secondary'
              >
                Delete
              </Button>
              <Button
                form='editWell'
                size='small'
                variant='contained'
                color='primary'
                type='submit'
              >
                Update
              </Button>
            </div>
          )}
          {value === 1 && hasPermissions && (
            <div style={{ alignSelf: 'center', display: 'flex', gap: 8 }}>
              <Button
                size='small'
                variant='contained'
                onClick={() => setMapEditMode((prev) => !prev)}
                style={{
                  backgroundColor: mapEditMode ? '#2e7d32' : '#555',
                  color: '#fff',
                  fontSize: '0.75rem',
                }}
              >
                {mapEditMode ? 'Done Editing' : 'Edit Map'}
              </Button>
              <Button
                size='small'
                variant='contained'
                onClick={() => wellKmzUploadRef.current?.click()}
                style={{ backgroundColor: '#555', color: '#fff', fontSize: '0.75rem' }}
              >
                Upload KMZ/KML
              </Button>
            </div>
          )}
          {value === 2 && (
            <div style={{ alignSelf: 'center' }}>
              <Button
                form='updatePlat'
                size='small'
                variant='contained'
                color='primary'
                type='submit'
              >
                Update
              </Button>
            </div>
          )}
        </Toolbar>
      </AppBar>
      <div style={{ paddingTop: '65px' }}>
        {value === 0 && (
          <TabContainer>
            <EditWell
              deleteModalOpen={deleteModalOpen}
              setDeleteModalOpen={setDeleteModalOpen}
              match={match}
              history={history}
            />
          </TabContainer>
        )}
        {value === 1 && (
          <TabContainer>
            {wellMapLoading ? <LoadingView /> : (
              <WellMapCard
                latitude={well?.latitude}
                longitude={well?.longitude}
                latitudeBottom={well?.latitudeBottom}
                longitudeBottom={well?.longitudeBottom}
                trueVerticalDepth={well?.trueVerticalDepth}
                name={well?.name}
                land={well?.landId}
                totalDepth={well?.totalDepth}
                upperPerf={well?.upperPerf}
                lowerPerf={well?.lowerPerf}
                perfLength={well?.perfLength}
                tracts={tracts}
                uploadRef={wellKmzUploadRef}
                editMode={mapEditMode}
                onSurfacePositionChange={(ll) => saveWellPoint('surface', ll)}
                onBottomPositionChange={(ll) => saveWellPoint('bottom', ll)}
                onTractPolygonChange={handleTractPolygonChange}
                onCoordsExtracted={(surface, bottom) => {
                  saveWellCoords({
                    variables: {
                      id: well.id,
                      apiNumber: well.apiNumber,
                      latitude: String(surface.lat),
                      longitude: String(surface.lng),
                      latitudeBottom: String(bottom.lat),
                      longitudeBottom: String(bottom.lng),
                    },
                  })
                    .then(() => enqueueSnackbar('Well coordinates saved!', { variant: 'success' }))
                    .catch((err) => enqueueSnackbar(err.toString(), { variant: 'error' }));
                }}
              />
            )}
          </TabContainer>
        )}
        {value === 2 && (
          <TabContainer>
            <PlatMap match={match} history={history} enqueueSnackbar={enqueueSnackbar} />
          </TabContainer>
        )}
      </div>
    </div>
  );
};

export default WellTabPage;
